'use client'

import { AlertTriangle, Check, ChevronDown, MoreHorizontal } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@client/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@client/components/ui/tooltip'
import { useFooter, type FooterAction, type FooterSubmenuItem } from '@client/providers/footer-provider'

function variantClass(variant: FooterAction['variant']): 'default' | 'outline' | 'destructive' {
  if (variant === 'primary') return 'default'
  if (variant === 'destructive') return 'destructive'
  return 'outline'
}

function SubmenuItemRow({ item, indent = false }: { item: FooterSubmenuItem; indent?: boolean }) {
  const isError = item.variant === 'error'
  return (
    <DropdownMenuItem
      onClick={item.onClick}
      disabled={item.disabled}
      // Override Radix's data-disabled styling for the error case so the
      // explanatory text stays readable instead of fading to 50% opacity.
      className={`flex items-start gap-2 ${indent ? 'pl-3' : ''} ${isError ? 'data-[disabled]:opacity-100' : ''}`}
      data-testid={`footer-submenu-item-${item.key}`}
    >
      <div className="w-4 mt-0.5 flex-shrink-0">
        {item.selected ? (
          <Check className="w-4 h-4 text-primary" />
        ) : isError ? (
          <AlertTriangle className="w-4 h-4 text-red-600" />
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${isError ? 'text-red-700 dark:text-red-400' : ''}`}>{item.label}</div>
        {item.description && (
          <div
            className={`text-xs truncate ${isError ? 'text-red-600/80 dark:text-red-400/80' : 'text-muted-foreground'}`}
          >
            {item.description}
          </div>
        )}
      </div>
    </DropdownMenuItem>
  )
}

// ── Inline rendering ────────────────────────────────────────────────────────

function InlineActionButton({ action }: { action: FooterAction }) {
  const hasSubmenu = !!action.submenu && action.submenu.items.length > 0
  const { label, icon: Icon, iconClassName, onClick, disabled, title, variant } = action
  const iconClass = `w-4 h-4 mr-2${iconClassName ? ` ${iconClassName}` : ''}`

  // When an action has a submenu, the whole button is the submenu trigger —
  // a single tap reveals the options. We append a small chevron as an
  // affordance so the user knows there's more.
  const body =
    hasSubmenu && action.submenu ? (
      <span className="flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={variantClass(variant)}
              size="sm"
              disabled={disabled}
              data-testid={action['data-testid']}
              aria-label={action.submenu.label}
            >
              {Icon && <Icon className={iconClass} />}
              {label}
              <ChevronDown className="w-4 h-4 ml-1.5 -mr-1 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" sideOffset={6} className="min-w-[14rem]">
            {action.submenu.items.map((item) => (
              <SubmenuItemRow key={item.key} item={item} />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    ) : (
      <span className="flex-shrink-0">
        <Button
          variant={variantClass(variant)}
          size="sm"
          onClick={onClick}
          disabled={disabled}
          data-testid={action['data-testid']}
        >
          {Icon && <Icon className={iconClass} />}
          {label}
        </Button>
      </span>
    )

  // Disabled buttons swallow pointer events in the browser, which makes the
  // native `title` attribute unreliable. Wrap in a Radix Tooltip so the
  // "why is this disabled?" hint surfaces on hover over the wrapper span.
  // Enabled buttons skip the tooltip — once the action is available the user
  // doesn't need a justification.
  if (disabled && title) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{body}</TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="max-w-xs text-xs">
            {title}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  return body
}

// ── Overflow (collapsed) rendering ──────────────────────────────────────────

function CollapsedActionItem({ action, isLast }: { action: FooterAction; isLast: boolean }) {
  const { label, icon: Icon, iconClassName, onClick, disabled, submenu } = action
  const hasSubmenu = !!submenu && submenu.items.length > 0
  const iconClass = `w-4 h-4 mr-2${iconClassName ? ` ${iconClassName}` : ''}`
  return (
    <>
      <DropdownMenuItem onClick={onClick} disabled={disabled} data-testid={action['data-testid']}>
        {Icon && <Icon className={iconClass} />}
        {label}
      </DropdownMenuItem>
      {/* When collapsed, render the submenu items inline instead of as a
          nested DropdownMenuSub — nested submenus open to the side, which
          slides off-screen on narrow viewports. A flat list with a small
          section header reads cleanly on both mobile and desktop. */}
      {hasSubmenu && (
        <>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold pt-2 pb-1">
            {submenu.label}
          </DropdownMenuLabel>
          {submenu.items.map((item) => (
            <SubmenuItemRow key={item.key} item={item} indent />
          ))}
          {!isLast && <DropdownMenuSeparator />}
        </>
      )}
    </>
  )
}

function OverflowActionsMenu({ actions }: { actions: FooterAction[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="flex-shrink-0" data-testid="footer-actions-overflow">
          <MoreHorizontal className="w-4 h-4 mr-2" />
          Actions
        </Button>
      </DropdownMenuTrigger>
      {/* Drop-up: footer sits at the bottom, so open above the trigger. */}
      <DropdownMenuContent side="top" align="end" sideOffset={6} className="min-w-[14rem] max-w-[calc(100vw-1.5rem)]">
        {actions.map((a, i) => (
          <CollapsedActionItem key={a.key} action={a} isLast={i === actions.length - 1} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Footer ──────────────────────────────────────────────────────────────────

/**
 * Fixed bottom action bar driven by `footer-provider`. Auto-hides when no
 * page has registered content (unless `alwaysVisible` is set).
 *
 * Responsive overflow:
 *   • By default every registered action renders inline (split-button if it
 *     has a `submenu`, otherwise a plain button).
 *   • A hidden phantom row sits inside the content area at its natural width.
 *     A ResizeObserver fires when either the container or the phantom changes
 *     size. When the phantom's natural width exceeds the available content
 *     area, ALL action buttons (including any split-buttons) collapse into a
 *     single "Actions" drop-up. Submenus become nested sub-menus there.
 *
 * The `leading` and `extra` slots stay inline regardless — they are typically
 * static text (selection count) or always-on widgets whose collapse behavior
 * is the caller's responsibility.
 */
export function GlobalFooter() {
  const { actions, leading, extra, alwaysVisible } = useFooter()
  const footerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const phantomRef = useRef<HTMLDivElement>(null)
  const [collapse, setCollapse] = useState(false)

  const hasContent = actions.length > 0 || !!leading || !!extra
  const visible = hasContent || alwaysVisible

  // Mirror the header's pattern: keep --footer-h in sync with the rendered
  // height so pages can offset bottom padding via pb-[var(--footer-h,0px)].
  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty('--footer-h', '0px')
      return
    }
    const el = footerRef.current
    if (!el) return
    const update = () => document.documentElement.style.setProperty('--footer-h', `${el.offsetHeight}px`)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.setProperty('--footer-h', '0px')
    }
  }, [visible])

  // Reactive overflow detection. The phantom and the content area share the
  // same parent box, so the phantom's natural width is directly comparable to
  // the content area's available width — regardless of whether actions are
  // currently inline or already collapsed (no flip-flop loop).
  useLayoutEffect(() => {
    if (!visible || actions.length === 0) {
      setCollapse(false)
      return
    }
    const content = contentRef.current
    const phantom = phantomRef.current
    if (!content || !phantom) return
    const check = () => {
      // 8px slack to avoid hysteresis fights with sub-pixel layout rounding.
      const SLACK = 8
      const fits = phantom.scrollWidth <= content.clientWidth - SLACK
      setCollapse(!fits)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(content)
    ro.observe(phantom)
    return () => ro.disconnect()
  }, [visible, actions, leading, extra])

  if (!visible) return null

  return (
    <div
      ref={footerRef}
      role="contentinfo"
      data-testid="global-footer"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm shadow-[0_-2px_8px_rgba(0,0,0,0.04)] pb-[env(safe-area-inset-bottom,0px)]"
    >
      <div className="mx-auto max-w-screen-2xl px-3 sm:px-6 py-2">
        <div ref={contentRef} className="relative">
          {/* Phantom: full-width natural row, invisible, doesn't take part in
              the visible flex layout. Its scrollWidth answers "would all the
              actions fit if rendered inline?" */}
          <div
            ref={phantomRef}
            aria-hidden
            className="absolute left-0 top-0 invisible pointer-events-none flex items-center gap-2 sm:gap-3 whitespace-nowrap"
          >
            {leading && <div className="flex items-center gap-2">{leading}</div>}
            {(extra || actions.length > 0) && (
              <div className="flex items-center gap-2">
                {extra}
                {actions.map((a) => (
                  // Strip data-testid: the phantom row is a measurement-only
                  // duplicate of the visible row and shouldn't appear to
                  // Playwright's getByTestId (would trigger strict-mode
                  // multi-match errors).
                  <InlineActionButton key={`m-${a.key}`} action={{ ...a, 'data-testid': undefined }} />
                ))}
              </div>
            )}
          </div>

          {/* Visible row */}
          <div className="flex items-center gap-2 sm:gap-3">
            {leading && <div className="flex items-center gap-2 min-w-0 truncate">{leading}</div>}
            {(extra || actions.length > 0) && (
              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                {extra}
                {collapse
                  ? actions.length > 0 && <OverflowActionsMenu actions={actions} />
                  : actions.map((a) => <InlineActionButton key={a.key} action={a} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
